import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { prescribeEnrollPractice } from 'api/Prescribe';

import { useGetVeradigmPractice } from 'lib/hooks/queries/eprescribe/useGetVeradigmPractice';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { showAlert } from 'components/shared/Alert/Alert';
import Loader from 'components/shared/Loader/Loader';

import ActivationScreen from './components/ActivationScreen';
import EprescribeConfirmationModal from './components/EprescribeConfirmationModal';
import StaffPrescribeTable from './components/StaffPrescribeTable';

const WizardEprescribe = ({
  setHeaderButtonProps,
  addFooterActions,
  mutateOrchestratorData,
  isAdmin,
  onNext
}) => {
  const [isActivated, setIsActivated] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { practiceId } = useOutletContext();
  const navigate = useNavigate();

  const {
    data: veradigmData,
    isLoading,
    refetch
  } = useGetVeradigmPractice({
    params: { practice_id: Number(practiceId) },
    dependencies: [practiceId]
  });

  const mutateActivation = useCustomMutation({
    mutationFn: () => prescribeEnrollPractice(navigate, { practiceId: Number(practiceId) }),
    onSuccess: (res) => {
      if (res.error) {
        showAlert({
          color: 'danger',
          message: res?.error ?? 'Activation failed. Please try again.'
        });
        return;
      }
      if (res?.requirements && res?.requirements?.length > 0) {
        showAlert({
          color: 'danger',
          message: res?.requirements?.[0]
        });
      } else {
        showAlert({
          color: 'success',
          message: 'e-Prescribe activated successfully.'
        });
        setIsActivated(true);
        refetch();
      }
    }
  });

  const handleMarkCompleted = () => {
    mutateOrchestratorData({ setupVeradigm: false });
    setShowConfirmModal(false);
  };

  const handleContinueActivation = () => {
    setShowConfirmModal(false);
  };

  const handleSkip = () => {
    setShowConfirmModal(false);
    onNext();
  };

  useEffect(() => {
    setHeaderButtonProps(null);

    if (veradigmData?.enrolled || isActivated) {
      // After activation, show Continue button
      addFooterActions([
        {
          text: 'Continue',
          onClick: () => {
            mutateOrchestratorData({ setupVeradigm: true });
          }
        }
      ]);
    } else {
      // Before activation, show Continue button that opens modal
      addFooterActions([
        {
          text: 'Continue',
          onClick: () => {
            setShowConfirmModal(true);
          }
        }
      ]);
    }
  }, [
    veradigmData?.enrolled,
    isActivated,
    setHeaderButtonProps,
    addFooterActions,
    mutateOrchestratorData
  ]);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader color="primary" />
      </div>
    );
  }

  if (!veradigmData?.enrolled && !isActivated) {
    return (
      <>
        <ActivationScreen
          onActivate={() => mutateActivation.mutate()}
          isLoading={mutateActivation?.isPending || mutateActivation?.isLoading}
        />
        <EprescribeConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onMarkCompleted={handleMarkCompleted}
          onContinueActivation={handleContinueActivation}
          onSkip={handleSkip}
          isLoadingActivation={mutateActivation?.isPending || mutateActivation?.isLoading}
        />
      </>
    );
  }

  return (
    <div className="w-full flex-shrink-0">
      <StaffPrescribeTable isAdmin={isAdmin} />
    </div>
  );
};

export default WizardEprescribe;
